import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ShieldCheck,
  Cpu,
  BarChart3,
  Bot,
  ClipboardCheck,
  TrendingUp,
  Bed,
  Heart,
} from "lucide-react";
import TechBackground from "@/components/fx/TechBackground";
import CountUp from "@/components/fx/CountUp";
import TiltCard from "@/components/fx/TiltCard";
import TypeRotator from "@/components/fx/TypeRotator";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
    >
      <TechBackground density={55} />
      {/* Ambient tech glow */}
      <div
        aria-hidden
        className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-[#3B82F6]/5 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-20 right-0 w-[400px] h-[400px] rounded-full bg-[#1E3A8A]/5 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#3B82F6]/40 text-xs font-mono-axis uppercase tracking-widest text-[#0369A1] mb-8 bg-[#3B82F6]/5">
              <span className="relative w-1.5 h-1.5 rounded-full bg-[#3B82F6] text-[#3B82F6] live-dot" />
              Healthcare · Data · IA
            </div>

            <h1
              data-testid="hero-title"
              className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-[#111827]"
            >
              Transformando a{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Saúde</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 lg:bottom-2 h-2 lg:h-3 bg-[#1E3A8A]/15"
                />
              </span>{" "}
              através da{" "}
              <span className="text-shimmer">Tecnologia</span>.
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base md:text-lg text-gray-600 leading-relaxed max-w-xl"
            >
              Healthtech especializada no ecossistema{" "}
              <span className="text-[#111827] font-medium">ERP Tasy</span>.
              Desenvolvemos 14+ produtos e soluções — do motor de faturamento (SYNIR) e BI (Wely) a assistente
              clínico com IA (EIRAI) —{" "}
              <span className="text-[#111827] font-medium">
                e customizamos tudo conforme a necessidade da sua instituição
              </span>
              .
            </p>

            <p
              data-testid="hero-type-rotator"
              className="mt-5 min-h-[2.5rem] sm:min-h-[1.5rem] font-mono-axis text-sm text-[#1E3A8A]"
            >
              <span className="text-gray-400">&gt; </span>
              <TypeRotator
                words={[
                  "SYNIR: a conta se audita, fecha e protocola sozinha",
                  "Wely: indicadores do Tasy em tempo real",
                  "MUNINN: IA lendo milhares de glosas em segundos",
                  "EIRAI: assistente clínico com IA",
                ]}
              />
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/contato"
                data-testid="hero-cta-demo"
                className="group relative overflow-hidden inline-flex items-center gap-2 px-7 py-4 rounded-md bg-[#1E3A8A] text-white text-base font-medium hover:bg-[#172554] transition-[background-color,box-shadow] hover:shadow-[0_0_32px_rgba(59,130,246,0.6)]"
              >
                <span aria-hidden className="absolute inset-y-0 -left-1/2 w-1/3 bg-white/20 skew-x-12 sweep" />
                <span className="relative">Solicitar demonstração</span>
                <ArrowUpRight className="relative w-4 h-4 transition-transform group-hover:rotate-45" />
              </Link>
            </div>

            {/* Trust bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-14 grid grid-cols-3 gap-6 max-w-xl"
            >
              <div>
                <p className="font-display text-3xl font-semibold text-[#111827]">
                  <CountUp to={14} data-testid="hero-stat-solutions" /><span className="text-[#3B82F6]">+</span>
                </p>
                <p className="text-xs font-mono-axis uppercase tracking-widest text-gray-500 mt-1">
                  Soluções
                </p>
              </div>
              <div>
                <p className="font-display text-3xl font-semibold text-[#111827]">
                  <CountUp to={99.9} decimals={1} /><span className="text-[#3B82F6]">%</span>
                </p>
                <p className="text-xs font-mono-axis uppercase tracking-widest text-gray-500 mt-1">
                  Uptime
                </p>
              </div>
              <div>
                <p className="font-display text-3xl font-semibold text-[#111827]">
                  LGPD
                </p>
                <p className="text-xs font-mono-axis uppercase tracking-widest text-gray-500 mt-1">
                  Compliance
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: system mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            {/* Offset frames */}
            <div
              aria-hidden
              className="absolute -top-4 -right-4 lg:-top-6 lg:-right-6 w-full h-full bg-[#1E3A8A]"
            />
            <div
              aria-hidden
              className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 w-32 h-32 border-2 border-[#3B82F6]"
            />

            {/* Main dashboard mockup */}
            <TiltCard max={7} className="group">
            <div className="relative bg-[#0B1220] border border-gray-800 shadow-2xl overflow-hidden">
              <div aria-hidden className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
                <div className="scanline" />
              </div>
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800">
                <span className="w-2.5 h-2.5 rounded-full bg-[#1E3A8A]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" />
                <span className="w-2.5 h-2.5 rounded-full bg-gray-600" />
                <span className="ml-3 font-mono-axis text-[10px] uppercase tracking-widest text-gray-500 truncate">
                  Wely <span className="text-gray-700">·</span>{" "}
                  <span className="text-gray-400">Painel gerencial</span>
                </span>
                <span className="ml-auto flex items-center gap-2 font-mono-axis text-[10px] text-[#3B82F6] shrink-0">
                  <span className="relative w-1.5 h-1.5 rounded-full bg-[#3B82F6] text-[#3B82F6] live-dot" />
                  live
                </span>
              </div>

              <div className="p-4 lg:p-5 space-y-4">
                {/* KPIs */}
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { icon: Bed, label: "Ocupação", value: "87%", c: "#3B82F6" },
                    { icon: Heart, label: "TMP", value: "4.2d", c: "#06B6D4" },
                    { icon: TrendingUp, label: "Receita", value: "R$ 12M", c: "#1E3A8A" },
                  ].map((k, i) => {
                    const Icon = k.icon;
                    return (
                      <motion.div
                        key={k.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + 0.1 * i, duration: 0.5 }}
                        className="bg-[#111827] border border-gray-800 p-3"
                      >
                        <Icon
                          className="w-3 h-3 mb-1.5"
                          style={{ color: k.c }}
                          strokeWidth={1.75}
                        />
                        <p className="font-display text-base lg:text-lg font-semibold text-white tabular-nums">
                          {k.value}
                        </p>
                        <p className="mt-0.5 font-mono-axis text-[8px] uppercase tracking-widest text-gray-500">
                          {k.label}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Chart */}
                <div className="bg-[#111827] border border-gray-800 p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-mono-axis text-[8px] uppercase tracking-widest text-gray-500">
                        Faturamento · 12m
                      </p>
                      <p className="mt-0.5 text-[10px] text-white">
                        <span className="text-[#3B82F6] font-medium">+14.8%</span>
                        <span className="text-gray-500"> vs ano anterior</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 font-mono-axis text-[8px] text-gray-500">
                      <span className="w-2 h-2 bg-[#3B82F6]" />
                      <span className="w-2 h-2 bg-[#1E3A8A]" />
                    </div>
                  </div>
                  <div className="relative h-16 lg:h-20 flex items-end gap-1">
                    {[32, 48, 40, 66, 55, 78, 62, 88, 72, 94, 80, 96].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{
                          duration: 0.7,
                          delay: 0.7 + 0.04 * i,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="flex-1 origin-bottom"
                        style={{
                          backgroundColor: i > 8 ? "#1E3A8A" : i > 4 ? "#06B6D4" : "#3B82F6",
                          minHeight: 2,
                        }}
                      />
                    ))}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="sweep absolute inset-y-0 w-1/6 bg-gradient-to-r from-transparent via-[#3B82F6]/10 to-transparent" />
                    </div>
                  </div>
                </div>

                {/* Product suite pills */}
                <div className="flex items-center gap-1.5">
                  {[
                    { name: "Wely", Icon: BarChart3, active: true },
                    { name: "HUGGIN", Icon: ClipboardCheck },
                    { name: "EIRAI", Icon: Bot },
                  ].map((p, i) => (
                    <motion.div
                      key={p.name}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.2 + 0.1 * i, duration: 0.4 }}
                      className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 border text-[9px] font-medium ${
                        p.active
                          ? "bg-[#3B82F6]/10 border-[#3B82F6]/40 text-[#3B82F6]"
                          : "border-gray-800 text-gray-500"
                      }`}
                    >
                      <p.Icon className="w-3 h-3" strokeWidth={1.75} />
                      <span className="font-mono-axis uppercase tracking-widest">
                        {p.name}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            </TiltCard>

            {/* Suite chip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="absolute -bottom-6 -left-6 lg:-bottom-8 lg:-left-8 bg-white border border-gray-200 shadow-lg p-4 max-w-[280px] hidden md:block"
            >
              <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500">
                Suíte Áxis
              </p>
              <p className="mt-1 font-display text-base font-medium text-[#111827] leading-snug">
                SYNIR · Wely · HUGGIN · MUNINN · EIRAI · EscalaMed · Autorizações · Assinatura · Anexos
              </p>
              <div className="mt-3 flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 bg-[#3B82F6] tech-pulse"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Security chip */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -top-6 right-8 lg:right-16 bg-[#111827] text-white p-4 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-[#3B82F6]" />
                <div>
                  <p className="text-xs font-mono-axis uppercase tracking-widest text-gray-400">
                    Segurança
                  </p>
                  <p className="text-sm font-medium">LGPD compliant</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom marquee-like tech row */}
      <div className="border-y border-gray-200 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center gap-8 overflow-x-auto">
          <span className="text-xs font-mono-axis uppercase tracking-widest text-gray-500 whitespace-nowrap">
            Stack corporativa
          </span>
          {["Tasy", "HL7 / FHIR", "Power BI", "Azure", "AWS", "Python", "React", "SQL Server"].map(
            (t) => (
              <span
                key={t}
                className="text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                {t}
              </span>
            )
          )}
          <Cpu className="w-4 h-4 text-[#3B82F6] shrink-0" />
        </div>
      </div>
    </section>
  );
}
